import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { FolderUp, GitPullRequest } from 'lucide-react';
import { Button, Callout, Input, Select } from '@/shared/ui';
import patterns from '@/shared/ui/styles/patterns.module.css';
import {
  registryApi,
  type SkillChange,
  type SkillChangeKind,
  type SkillValidationResult,
} from '../../api/registryApi';
import { registryKeys } from '../../queryKeys';
import {
  changedPaths,
  jsonError,
  orderedPaths,
  relativizeFolder,
  slugifyDirName,
} from '../../utils/skillEditor';
import styles from './SkillWriteBackPanel.module.css';

const MAX_FILE_BYTES = 512 * 1024;

function errorMessage(err: unknown): string {
  const message = (err as { response?: { data?: { error?: { message?: string } } } })?.response
    ?.data?.error?.message;
  return message ?? 'Something went wrong — try again.';
}

async function readPickedFiles(
  list: FileList,
): Promise<{ entries: Array<{ path: string; text: string }>; skipped: string[] }> {
  const entries: Array<{ path: string; text: string }> = [];
  const skipped: string[] = [];
  for (const file of Array.from(list)) {
    const path = (file as File & { webkitRelativePath?: string }).webkitRelativePath || file.name;
    if (file.size > MAX_FILE_BYTES) {
      skipped.push(`${path} (too large)`);
      continue;
    }
    const text = await file.text();
    if (text.includes('\u0000')) {
      skipped.push(`${path} (binary)`);
      continue;
    }
    entries.push({ path, text });
  }
  return { entries, skipped };
}

function Issues({ result }: { result: SkillValidationResult }) {
  return (
    <>
      {result.errors.length > 0 && (
        <Callout
          variant="error"
          title={`${result.errors.length} problem${result.errors.length === 1 ? '' : 's'} to fix before a pull request can open`}
        >
          <ul className={styles.issues}>
            {result.errors.map((e, i) => (
              <li key={i}>
                {e.path && <span className={styles.issuePath}>{e.path}: </span>}
                {e.message}
              </li>
            ))}
          </ul>
        </Callout>
      )}
      {result.warnings.length > 0 && (
        <Callout variant="warning" title="Worth knowing">
          <ul className={styles.issues}>
            {result.warnings.map((w, i) => (
              <li key={i}>
                {w.path && <span className={styles.issuePath}>{w.path}: </span>}
                {w.message}
              </li>
            ))}
          </ul>
        </Callout>
      )}
      {result.ok && result.summary && (
        <p className={styles.summaryLine}>
          Passes: <code>{result.summary.key}</code> · {result.summary.mode} ·{' '}
          {result.summary.triggerCount} trigger
          {result.summary.triggerCount === 1 ? '' : 's'} · {result.summary.methodFormat} ·{' '}
          {result.summary.referenceCount} reference
          {result.summary.referenceCount === 1 ? '' : 's'}
          {result.summary.dormant ? ' · will load dormant' : ''}
        </p>
      )}
    </>
  );
}

/**
 * Edit, upload a new version of, or create a skill — and turn it into a pull request. Nothing here
 * writes to disk: the files are validated by agent-suite first, then committed to a fresh branch
 * and opened as a PR for a human to merge. Three modes share one flow:
 *   edit    — the current files in textareas (an edit sends every file the editor holds);
 *   upload  — a folder replaces the directory (files no longer present are deleted in the PR);
 *   create  — a folder becomes a new directory under a chosen module.
 */
export function SkillWriteBackPanel({
  mode,
  owner: initialOwner,
  dirName: initialDirName,
  owners,
  initialFiles,
  onDone,
  onCancel,
}: {
  mode: SkillChangeKind;
  owner: string;
  dirName: string | null;
  /** Module choices for create mode. */
  owners?: string[];
  initialFiles?: Record<string, string>;
  onDone?: (change: SkillChange) => void;
  onCancel: () => void;
}) {
  const queryClient = useQueryClient();
  const [owner, setOwner] = useState(initialOwner);
  const [dirName, setDirName] = useState(initialDirName ?? '');
  const [files, setFiles] = useState<Record<string, string>>(initialFiles ?? {});
  const [skipped, setSkipped] = useState<string[]>([]);
  const [title, setTitle] = useState('');
  const [summary, setSummary] = useState('');
  const [validation, setValidation] = useState<SkillValidationResult | null>(null);
  const [opened, setOpened] = useState<SkillChange | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  // Folder picking needs a non-standard attribute React's types do not know about.
  useEffect(() => {
    fileInput.current?.setAttribute('webkitdirectory', '');
  }, []);

  useEffect(() => {
    if (initialFiles) setFiles(initialFiles);
  }, [initialFiles]);

  const paths = useMemo(() => orderedPaths(Object.keys(files)), [files]);
  const manifestError =
    files['manifest.json'] !== undefined ? jsonError(files['manifest.json']) : null;
  const changed = useMemo(
    () => (initialFiles ? changedPaths(initialFiles, files) : paths),
    [initialFiles, files, paths],
  );
  const ready =
    paths.length > 0 &&
    dirName.trim().length > 0 &&
    !manifestError &&
    (mode !== 'edit' || changed.length > 0);

  const validate = useMutation({
    mutationFn: () => registryApi.validate({ owner, dirName, files }),
    onSuccess: (result) => setValidation(result),
  });

  const propose = useMutation({
    mutationFn: () =>
      registryApi.propose({
        owner,
        dirName,
        kind: mode,
        title: title.trim(),
        summary: summary.trim() || undefined,
        files,
      }),
    onSuccess: (change) => {
      setOpened(change);
      queryClient.invalidateQueries({ queryKey: registryKeys.all });
      onDone?.(change);
    },
  });

  const edit = (path: string, text: string) => {
    setFiles((f) => ({ ...f, [path]: text }));
    setValidation(null);
  };

  const onPick = async (list: FileList | null) => {
    if (!list || list.length === 0) return;
    const { entries, skipped: dropped } = await readPickedFiles(list);
    const rel = relativizeFolder(entries);
    setFiles(rel.files);
    setSkipped(dropped);
    setValidation(null);
    if (mode === 'create' && rel.dirName && !dirName) setDirName(slugifyDirName(rel.dirName));
  };

  if (opened) {
    return (
      <div className={styles.panel}>
        <Callout
          icon={GitPullRequest}
          variant="success"
          title={`Pull request #${opened.prNumber} opened`}
        >
          <div className={styles.done}>
            <span>
              <code>
                {opened.owner}/{opened.dirName}
              </code>{' '}
              is on branch <code>{opened.branch}</code>. It goes live once a reviewer merges it, the
              merge is deployed, and the agent server restarts and picks it up; this row will say so
              then.
            </span>
            {opened.prUrl && (
              <a href={opened.prUrl} target="_blank" rel="noreferrer">
                Open the pull request ↗
              </a>
            )}
          </div>
        </Callout>
        <div className={styles.actions}>
          <Button size="sm" variant="secondary" onClick={onCancel}>
            Close
          </Button>
        </div>
      </div>
    );
  }

  const heading =
    mode === 'edit' ? 'Edit skill' : mode === 'upload' ? 'Upload a new version' : 'Create a skill';

  return (
    <div className={styles.panel}>
      <div className={styles.heading}>
        <span className={styles.headingTitle}>{heading}</span>
        <span className={patterns.cellMuted}>
          Validated, then a pull request. Nothing changes until it merges.
        </span>
      </div>

      {mode === 'create' && (
        <div className={styles.fields}>
          <Select
            label="Module"
            value={owner}
            options={(owners ?? [initialOwner]).map((o) => ({
              value: o,
              label: o === 'shared' ? 'shared (every agent)' : o,
            }))}
            onChange={(event) => {
              setOwner(event.target.value);
              setValidation(null);
            }}
          />
          <Input
            label="Directory name"
            value={dirName}
            placeholder="ad-budget-pacing"
            hint="lower-case, dashes; becomes modules/<module>/skills/<name>"
            onChange={(event) => {
              setDirName(slugifyDirName(event.target.value));
              setValidation(null);
            }}
          />
        </div>
      )}

      {mode !== 'edit' && (
        <div className={styles.dropzone}>
          <FolderUp size={20} aria-hidden="true" />
          <span>Pick the skill folder: manifest.json, SKILL.md or method.md, references/…</span>
          <input
            ref={fileInput}
            className={styles.hiddenInput}
            type="file"
            multiple
            aria-label="Skill folder"
            onChange={(event) => void onPick(event.target.files)}
          />
          <Button size="sm" variant="secondary" onClick={() => fileInput.current?.click()}>
            Choose folder
          </Button>
          {paths.length > 0 && (
            <div className={styles.picked}>
              {paths.map((p) => (
                <span key={p}>{p}</span>
              ))}
            </div>
          )}
          {skipped.length > 0 && (
            <span className={patterns.cellMuted}>Skipped: {skipped.join(', ')}</span>
          )}
        </div>
      )}

      {mode === 'edit' && (
        <div className={styles.fileList}>
          {paths.map((path) => {
            const isManifest = path === 'manifest.json';
            const isMethod = path === 'SKILL.md' || path === 'method.md';
            return (
              <div key={path} className={styles.file}>
                <div className={styles.fileHead}>
                  <span>{path}</span>
                  <span className={styles.fileMeta}>
                    {initialFiles && initialFiles[path] !== files[path] ? 'changed' : 'unchanged'}
                    {isManifest && manifestError ? ` · invalid JSON: ${manifestError}` : ''}
                  </span>
                </div>
                <textarea
                  className={`${styles.editor} ${isMethod ? styles.editorTall : ''}`}
                  aria-label={path}
                  spellCheck={!isManifest}
                  value={files[path]}
                  onChange={(event) => edit(path, event.target.value)}
                />
              </div>
            );
          })}
        </div>
      )}

      <div className={styles.fields}>
        <Input
          label="Pull request title"
          value={title}
          placeholder={
            mode === 'edit'
              ? `Tighten ${dirName || 'the skill'}'s method`
              : `Add ${dirName || 'the skill'}`
          }
          onChange={(event) => setTitle(event.target.value)}
        />
        <div className={styles.fieldsWide}>
          <label className={patterns.fieldLabel} htmlFor="skill-change-summary">
            What changed and why
          </label>
          <textarea
            id="skill-change-summary"
            className={patterns.textarea}
            rows={3}
            value={summary}
            onChange={(event) => setSummary(event.target.value)}
          />
        </div>
      </div>

      {validate.isError && (
        <Callout variant="error" title="Could not validate">
          {errorMessage(validate.error)}
        </Callout>
      )}
      {propose.isError && (
        <Callout variant="error" title="Could not open the pull request">
          {errorMessage(propose.error)}
        </Callout>
      )}
      {validation && <Issues result={validation} />}

      <div className={styles.actions}>
        <Button
          size="sm"
          variant="secondary"
          disabled={!ready || validate.isPending}
          onClick={() => validate.mutate()}
        >
          {validate.isPending ? 'Validating…' : 'Validate'}
        </Button>
        <Button
          size="sm"
          disabled={!ready || !validation?.ok || title.trim().length < 3 || propose.isPending}
          onClick={() => propose.mutate()}
        >
          {propose.isPending ? 'Opening…' : 'Open pull request'}
        </Button>
        <span className={styles.spacer} />
        <Button size="sm" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}
